export type YaTidak = "ya" | "tidak";
export type StatusAsesmenGeriatri = "draft" | "final";

export interface PilihanLainnya {
  pilihan: string[];
  lainnya_text: string;
}

export interface TemuanFisik {
  temuan: string[];
  catatan: string;
}

export type HeadToToeKey =
  | "kepala_rambut"
  | "mata"
  | "telinga"
  | "hidung_tenggorokan"
  | "gigi_mulut"
  | "leher"
  | "thorax_paru"
  | "jantung"
  | "abdomen"
  | "ekstremitas"
  | "kulit";

export interface KognitifItems {
  orientasi: number | null;
  registrasi: number | null;
  atensi_kalkulasi: number | null;
  recall: number | null;
  bahasa_perintah: number | null;
}

export interface BarthelItems {
  makan: number | null;
  mandi: number | null;
  berhias: number | null;
  berpakaian: number | null;
  bak: number | null;
  bab: number | null;
  toilet: number | null;
  transfer: number | null;
  berjalan: number | null;
  tangga: number | null;
}

export type BarthelKey = keyof BarthelItems;

export interface NutrisiItems {
  asupan_makanan: number | null;
  kehilangan_bb: number | null;
  mobilitas: number | null;
  psikologis: number | null;
  neurologis: number | null;
  imt: number | null;
  lila: number | null;
}

export interface RisikoJatuhItems {
  riwayat_jatuh: number | null;
  status_mental: number | null;
  gangguan_penglihatan_berat: number | null;
  kebiasaan_berkemih: number | null;
  transfer: number | null;
  mobilitas: number | null;
}

export interface BarthelSumber {
  mode: "auto" | "auto_diedit" | "manual";
  asesmen_awal_ranap_keperawatan_id: number | null;
  examined_at: string | null;
  perawat_user_id: number | null;
  perawat_nama: string | null;
  ditarik_at: string | null;
}

export interface BarthelBlock extends BarthelItems {
  total: number | null;
  interpretasi: string | null;
  sumber?: BarthelSumber | null;
}

export interface AsesmenGeriatriContent {
  pendamping: { nama: string; hubungan: string };
  anamnesis: {
    keluhan_utama: {
      jatuh: boolean;
      sering_jatuh: boolean;
      tirah_baring: boolean;
      pusing: boolean;
      bingung: boolean;
      tidak_mau_makan: boolean;
      lainnya: boolean;
      lainnya_text: string;
      jatuh_tirah_baring?: boolean;
      pusing_bingung?: boolean;
    };
    rps: string;
    rpd: {
      hipertensi: boolean;
      diabetes_melitus: boolean;
      penyakit_jantung_koroner: boolean;
      stroke: boolean;
      asma_ppok: boolean;
      gagal_ginjal_kronis: boolean;
      lain_lain: boolean;
      lain_lain_text: string;
    };
  };
  pemfis: {
    tanda_vital: {
      td_berbaring_sistolik: number | null;
      td_berbaring_diastolik: number | null;
      td_berdiri_sistolik: number | null;
      td_berdiri_diastolik: number | null;
      ortostatik_manual: YaTidak | null;

      ortostatik?: boolean | null;
    };
    head_to_toe: Record<HeadToToeKey, TemuanFisik>;
  };
  penunjang: {
    laboratorium: string;
    radiologi: string;
    terapi_saat_dirawat: string;
  };
  skrining: {
    kognitif: KognitifItems & { total: number | null; kategori: string | null };
    polifarmasi: { hasil: YaTidak | null; daftar_obat_aktif: string };
    status_fungsional: {
      barthel_awal: BarthelBlock;
      barthel_akhir: BarthelBlock;
    };
    nutrisi: NutrisiItems & { total: number | null; interpretasi: string | null };
    risiko_jatuh: RisikoJatuhItems & {
      total: number | null;
      kategori?: string | null;
      transfer_mobilitas_skor?: number | null;
    };
  };
  masalah: {
    delirium: boolean;
    falls: boolean;
    immobility: boolean;
    incontinence: boolean;
    malnutrition: boolean;
  };
  geriatric_syndrome: {
    malnutrisi: boolean;
    polifarmasi: boolean;
    fall_risk: boolean;
    pressure_ulcer: boolean;
    cognitive_impairment: boolean;
    frailty: boolean;
    incontinence: boolean;
  };
  diagnosa_klinis: string;
  rencana_intervensi: {
    medis: PilihanLainnya;
    rehab_medik: PilihanLainnya;
    gizi_klinik: PilihanLainnya;
    keperawatan: PilihanLainnya;
  };
}

export interface AsesmenGeriatriPihak {
  id: number;
  nama: string | null;
}

export interface AsesmenGeriatriTandaTangan {
  user: AsesmenGeriatriPihak;
  created_at: string | null;
}

export interface AsesmenGeriatriRecord {
  id: number;
  register_id: number;
  pasien_rm: string;
  unit_id: string | null;
  status: StatusAsesmenGeriatri;
  examined_at: string | null;
  content: AsesmenGeriatriContent | null;

  pmr_total: number | null;
  pmr_kategori: string | null;
  pmr_kategori_label: string | null;

  barthel_awal_total: number | null;
  barthel_awal_interpretasi: string | null;
  barthel_awal_interpretasi_label: string | null;
  barthel_awal_source_id: number | null;

  barthel_akhir_total: number | null;
  barthel_akhir_interpretasi: string | null;
  barthel_akhir_interpretasi_label: string | null;

  nutrisi_total: number | null;
  nutrisi_interpretasi: string | null;
  nutrisi_interpretasi_label: string | null;

  risiko_jatuh_total: number | null;
  risiko_jatuh_kategori: string | null;
  risiko_jatuh_kategori_label: string | null;
  ortostatik: boolean | null;

  creator_user_id: number | null;
  creator: AsesmenGeriatriPihak | null;
  signed_pembuat: boolean;
  signed_pembuat_at: string | null;

  ppja_user_id: number | null;
  ppja: AsesmenGeriatriPihak | null;
  signed_ppja: boolean;
  signed_ppja_at: string | null;

  dpjp_user_id: number | null;
  dpjp: AsesmenGeriatriPihak | null;
  signed_dpjp: boolean;
  signed_dpjp_at: string | null;

  perawat_user_id: number | null;
  perawat: AsesmenGeriatriPihak | null;
  signed_perawat: boolean;
  signed_perawat_at: string | null;

  gizi_user_id: number | null;
  gizi: AsesmenGeriatriPihak | null;
  signed_gizi: boolean;
  signed_gizi_at: string | null;

  ketua_tim_user_id: number | null;
  ketua_tim: AsesmenGeriatriPihak | null;
  signed_ketua_tim: boolean;
  signed_ketua_tim_at: string | null;

  konsulan?: AsesmenGeriatriTandaTangan[];

  created_at: string | null;
  updated_at: string | null;
}

export interface BarthelAwalPrefill {
  found: boolean;
  data: BarthelItems | null;
  meta:
    | (BarthelSumber & {
        lengkap: boolean;
        terisi: number;
        total: number | null;
        interpretasi: string | null;
        interpretasi_label: string | null;
      })
    | null;
  message?: string;
}

export const BARTHEL_KEYS: BarthelKey[] = [
  "makan",
  "mandi",
  "berhias",
  "berpakaian",
  "bak",
  "bab",
  "toilet",
  "transfer",
  "berjalan",
  "tangga"
];

export const HEAD_TO_TOE_KEYS: HeadToToeKey[] = [
  "kepala_rambut",
  "mata",
  "telinga",
  "hidung_tenggorokan",
  "gigi_mulut",
  "leher",
  "thorax_paru",
  "jantung",
  "abdomen",
  "ekstremitas",
  "kulit"
];

export function defaultBarthel(): BarthelBlock {
  return {
    makan: null,
    mandi: null,
    berhias: null,
    berpakaian: null,
    bak: null,
    bab: null,
    toilet: null,
    transfer: null,
    berjalan: null,
    tangga: null,
    total: null,
    interpretasi: null,
    sumber: null
  };
}

function defaultPilihanLainnya(): PilihanLainnya {
  return { pilihan: [], lainnya_text: "" };
}

function defaultHeadToToe(): Record<HeadToToeKey, TemuanFisik> {
  const out = {} as Record<HeadToToeKey, TemuanFisik>;
  HEAD_TO_TOE_KEYS.forEach(key => {
    out[key] = { temuan: [], catatan: "" };
  });
  return out;
}

export function defaultContent(): AsesmenGeriatriContent {
  return {
    pendamping: { nama: "", hubungan: "" },
    anamnesis: {
      keluhan_utama: {
        jatuh: false,
        sering_jatuh: false,
        tirah_baring: false,
        pusing: false,
        bingung: false,
        tidak_mau_makan: false,
        lainnya: false,
        lainnya_text: "",
        jatuh_tirah_baring: false,
        pusing_bingung: false
      },
      rps: "",
      rpd: {
        hipertensi: false,
        diabetes_melitus: false,
        penyakit_jantung_koroner: false,
        stroke: false,
        asma_ppok: false,
        gagal_ginjal_kronis: false,
        lain_lain: false,
        lain_lain_text: ""
      }
    },
    pemfis: {
      tanda_vital: {
        td_berbaring_sistolik: null,
        td_berbaring_diastolik: null,
        td_berdiri_sistolik: null,
        td_berdiri_diastolik: null,
        ortostatik_manual: null,
        ortostatik: null
      },
      head_to_toe: defaultHeadToToe()
    },
    penunjang: { laboratorium: "", radiologi: "", terapi_saat_dirawat: "" },
    skrining: {
      kognitif: {
        orientasi: null,
        registrasi: null,
        atensi_kalkulasi: null,
        recall: null,
        bahasa_perintah: null,
        total: null,
        kategori: null
      },
      polifarmasi: { hasil: null, daftar_obat_aktif: "" },
      status_fungsional: {
        barthel_awal: defaultBarthel(),
        barthel_akhir: defaultBarthel()
      },
      nutrisi: {
        asupan_makanan: null,
        kehilangan_bb: null,
        mobilitas: null,
        psikologis: null,
        neurologis: null,
        imt: null,
        lila: null,
        total: null,
        interpretasi: null
      },
      risiko_jatuh: {
        riwayat_jatuh: null,
        status_mental: null,
        gangguan_penglihatan_berat: null,
        kebiasaan_berkemih: null,
        transfer: null,
        mobilitas: null,
        total: null,
        kategori: null,
        transfer_mobilitas_skor: null
      }
    },
    masalah: {
      delirium: false,
      falls: false,
      immobility: false,
      incontinence: false,
      malnutrition: false
    },
    geriatric_syndrome: {
      malnutrisi: false,
      polifarmasi: false,
      fall_risk: false,
      pressure_ulcer: false,
      cognitive_impairment: false,
      frailty: false,
      incontinence: false
    },
    diagnosa_klinis: "",
    rencana_intervensi: {
      medis: defaultPilihanLainnya(),
      rehab_medik: defaultPilihanLainnya(),
      gizi_klinik: defaultPilihanLainnya(),
      keperawatan: defaultPilihanLainnya()
    }
  };
}

export function mergeContent(saved: unknown): AsesmenGeriatriContent {
  const def = defaultContent();
  const s = (saved && typeof saved === "object" ? saved : {}) as Record<string, any>;

  const out: AsesmenGeriatriContent = { ...def, ...(s as any) };

  out.pendamping = { ...def.pendamping, ...(s.pendamping || {}) };

  out.anamnesis = { ...def.anamnesis, ...(s.anamnesis || {}) };
  out.anamnesis.keluhan_utama = {
    ...def.anamnesis.keluhan_utama,
    ...((s.anamnesis || {}).keluhan_utama || {})
  };
  out.anamnesis.rpd = { ...def.anamnesis.rpd, ...((s.anamnesis || {}).rpd || {}) };

  out.pemfis = { ...def.pemfis, ...(s.pemfis || {}) };
  out.pemfis.tanda_vital = {
    ...def.pemfis.tanda_vital,
    ...((s.pemfis || {}).tanda_vital || {})
  };
  const savedHtt = (s.pemfis || {}).head_to_toe || {};
  out.pemfis.head_to_toe = defaultHeadToToe();
  HEAD_TO_TOE_KEYS.forEach(key => {
    const row = savedHtt[key] || {};
    out.pemfis.head_to_toe[key] = {
      temuan: Array.isArray(row.temuan) ? row.temuan : [],
      catatan: typeof row.catatan === "string" ? row.catatan : ""
    };
  });

  out.penunjang = { ...def.penunjang, ...(s.penunjang || {}) };

  const savedSkrining = s.skrining || {};
  out.skrining = { ...def.skrining, ...savedSkrining };
  out.skrining.kognitif = { ...def.skrining.kognitif, ...(savedSkrining.kognitif || {}) };
  out.skrining.polifarmasi = {
    ...def.skrining.polifarmasi,
    ...(savedSkrining.polifarmasi || {})
  };
  out.skrining.nutrisi = { ...def.skrining.nutrisi, ...(savedSkrining.nutrisi || {}) };
  out.skrining.risiko_jatuh = {
    ...def.skrining.risiko_jatuh,
    ...(savedSkrining.risiko_jatuh || {})
  };
  const savedFungsional = savedSkrining.status_fungsional || {};
  out.skrining.status_fungsional = {
    barthel_awal: { ...defaultBarthel(), ...(savedFungsional.barthel_awal || {}) },
    barthel_akhir: { ...defaultBarthel(), ...(savedFungsional.barthel_akhir || {}) }
  };

  out.masalah = { ...def.masalah, ...(s.masalah || {}) };
  out.geriatric_syndrome = { ...def.geriatric_syndrome, ...(s.geriatric_syndrome || {}) };
  out.diagnosa_klinis = typeof s.diagnosa_klinis === "string" ? s.diagnosa_klinis : "";

  const savedIntervensi = s.rencana_intervensi || {};
  out.rencana_intervensi = {
    medis: { ...defaultPilihanLainnya(), ...(savedIntervensi.medis || {}) },
    rehab_medik: { ...defaultPilihanLainnya(), ...(savedIntervensi.rehab_medik || {}) },
    gizi_klinik: { ...defaultPilihanLainnya(), ...(savedIntervensi.gizi_klinik || {}) },
    keperawatan: { ...defaultPilihanLainnya(), ...(savedIntervensi.keperawatan || {}) }
  };

  return out;
}
